import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Textarea,
  Select,
  Toggle,
  ExpandableSection,
  Header,
  Container,
  Grid,
  Slider,
  Alert
} from '@cloudscape-design/components'
import { DashboardWidget } from '../../../types'
import { useQuery, useConvex } from 'convex/react'
import { api } from '../../../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../../../providers/AmplifyAuthProvider'

interface ChartWidgetConfigProps {
  visible: boolean
  widget: DashboardWidget
  onConfirm: (updatedWidget: DashboardWidget) => void
  onDismiss: () => void
}

// Chart type options
const CHART_TYPE_OPTIONS = [
  { label: 'Line Chart', value: 'line' },
  { label: 'Area Chart', value: 'area' },
  { label: 'Bar Chart', value: 'bar' }
]

// Default visualization config
const DEFAULT_VISUALIZATION = {
  chartType: 'line',
  xAxis: {
    type: 'time',
    label: 'Time',
    timeZone: 'UTC'
  },
  yAxes: [{
    id: 'left',
    label: 'Value',
    unit: ''
  }],
  colors: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'],
  strokeWidth: 2,
  legend: {
    show: true,
    position: 'bottom'
  },
  tooltip: {
    show: true
  },
  zoom: {
    enabled: true,
    resetOnNewData: false
  },
  interpolateNulls: false,
  showDataPoints: false,
  connectNulls: false
}

export const ChartWidgetConfig: React.FC<ChartWidgetConfigProps> = ({
  visible,
  widget,
  onConfirm,
  onDismiss
}) => {
  const [title, setTitle] = useState(widget.title || '')
  const [description, setDescription] = useState(widget.description || '')
  const [visualization, setVisualization] = useState(widget.visualization || DEFAULT_VISUALIZATION)
  const [queries, setQueries] = useState(widget.queries || [])

  // Get current session for authenticated queries
  const { sessionId } = useAmplifyAuth()
  const convex = useConvex()

  // Asset hierarchy and properties state with global caching
  const [assetHierarchy, setAssetHierarchy] = useState<any[]>(() => {
    // Initialize from global cache if available
    return (window as any).__assetHierarchyCache?.data || []
  })
  const [assetProperties, setAssetProperties] = useState<Record<string, any[]>>(() => {
    // Initialize from global cache if available
    return (window as any).__assetPropertiesCache?.data || {}
  })
  const [isLoadingAssets, setIsLoadingAssets] = useState(false)
  const [isLoadingProperties, setIsLoadingProperties] = useState(false)
  const [assetHierarchyLoaded, setAssetHierarchyLoaded] = useState(() => {
    // Initialize from global cache state
    return (window as any).__assetHierarchyCache?.loaded || false
  })

  // Load asset hierarchy on component mount
  useEffect(() => {
    const loadAssets = async () => {
      if (!sessionId || assetHierarchyLoaded) {
        console.log('🔍 Asset hierarchy already loaded, skipping fetch')
        return // Skip if already loaded
      }

      try {
        setIsLoadingAssets(true)
        console.log('🔍 Loading asset hierarchy (not cached)')
        
        // Use the same approach as SmartPlant - get individual queries
        const factories = await convex.query(api["components/assets/index"].getFactories, { sessionId })
        const productionLines = await convex.query(api["components/assets/index"].getProductionLines, { sessionId })
        const equipment = await convex.query(api["components/assets/index"].getEquipmentComponents, { sessionId })
        
        console.log('🔍 Factories:', factories)
        console.log('🔍 Production Lines:', productionLines)
        console.log('🔍 Equipment:', equipment)
        
        // Transform equipment into asset options
        const transformedAssets = equipment?.map((eq: any) => {
          const line = productionLines?.find((pl: any) => pl._id === eq.production_line_id)
          const factory = factories?.find((f: any) => f._id === line?.factory_id)
          
          return {
            id: eq._id,
            name: eq.name || eq.equipment_name || 'Unknown Equipment',
            hierarchy: [
              factory?.name || 'Unknown Factory',
              line?.name || line?.line_name || 'Unknown Line',
              eq.name || eq.equipment_name || 'Unknown Equipment'
            ],
            external_asset_id: eq.external_asset_id
          }
        }) || []
        
        // Update both state and global cache
        setAssetHierarchy(transformedAssets)
        setAssetHierarchyLoaded(true)
        
        // Store in global cache to persist across component remounts
        ;(window as any).__assetHierarchyCache = {
          data: transformedAssets,
          loaded: true,
          timestamp: Date.now()
        }
        
        console.log('✅ Asset hierarchy cached globally')
      } catch (error) {
        console.error('Failed to load assets:', error)
        setAssetHierarchy([])
        setAssetHierarchyLoaded(false)
        
        // Clear global cache on error
        ;(window as any).__assetHierarchyCache = {
          data: [],
          loaded: false,
          timestamp: Date.now()
        }
      } finally {
        setIsLoadingAssets(false)
      }
    }

    loadAssets()
  }, [sessionId, assetHierarchyLoaded])

  // Load properties for a selected asset
  const loadAssetProperties = useCallback(async (assetId: string) => {
    if (!sessionId || !assetId || assetProperties[assetId]) {
      console.log('🔍 Properties for asset already cached, skipping fetch:', assetId)
      return assetProperties[assetId]
    }

    try {
      setIsLoadingProperties(true)
      console.log('🔍 Loading properties for asset:', assetId)
      
      // Use the existing getAssetDetailsWithProperties query
      const assetDetails = await convex.query(api["components/assets/index"].getAssetDetailsWithProperties, {
        assetId,
        assetType: 'equipment_component', // Correct asset type name
        sessionId
      })

      console.log('🔍 Asset details with properties:', assetDetails)
      console.log('🔍 Asset attributes:', assetDetails?.asset?.attributes)
      console.log('🔍 Properties in attributes:', assetDetails?.asset?.attributes?.properties)

      // Use categorized properties just like SmartPlant - prioritize measurements for charts
      const measurements = assetDetails?.categorizedProperties?.measurements || []
      const attributes = assetDetails?.categorizedProperties?.attributes || []
      
      console.log('🔍 Measurements found:', measurements.length)
      console.log('🔍 Attributes found:', attributes.length)
      console.log('🔍 Measurements:', measurements)
      console.log('🔍 Attributes:', attributes)
      
      // Transform measurements for the dropdown (most relevant for charts)
      const properties = measurements.map((item: any) => ({
        id: item.mapping?.external_property_id || item.id,
        name: item.name || 'Unknown Property',
        unit: item.unit || '',
        dataType: item.dataType || item.data_type || 'number',
        propertyType: item.property_type || 'measurement'
      }))
      
      // TODO: Add UI to also show attributes if user wants to chart them
      // For now, focus on measurements which are the time-series data points

      console.log('🔍 Transformed properties:', properties)

      // Update both state and global cache
      const updatedProperties = {
        ...assetProperties,
        [assetId]: properties
      }
      
      setAssetProperties(updatedProperties)
      
      // Store in global cache to persist across component remounts
      ;(window as any).__assetPropertiesCache = {
        data: updatedProperties,
        timestamp: Date.now()
      }

      console.log('✅ Asset properties cached globally')
      
      return properties
    } catch (error) {
      console.error('Failed to load properties:', error)
      return []
    } finally {
      setIsLoadingProperties(false)
    }
  }, [sessionId, assetProperties])

  const handleAddQuery = () => {
    const newQuery = {
      id: `query_${Date.now()}`,
      name: 'New Data Series',
      asset: {
        id: '',
        name: '',
        hierarchy: [],
        external_asset_id: ''
      },
      property: {
        id: '',
        name: '',
        unit: '',
        dataType: 'number',
        propertyType: 'measurement'
      },
      color: visualization.colors[queries.length % visualization.colors.length],
      visible: true,
      yAxis: 'left'
    }
    setQueries([...queries, newQuery])
  }

  const handleRemoveQuery = (queryId: string) => {
    setQueries(queries.filter(q => q.id !== queryId))
  }

  const handleQueryChange = (queryId: string, updates: any) => {
    setQueries(queries.map(query => 
      query.id === queryId 
        ? { ...query, ...updates }
        : query
    ))
  }

  const handleVisualizationChange = (field: string, value: any) => {
    setVisualization(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = () => {
    const updatedWidget: DashboardWidget = {
      ...widget,
      title: title.trim(),
      description: description.trim(),
      queries,
      visualization
    }
    
    onConfirm(updatedWidget)
  }

  const isValid = title.trim().length > 0

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header="Configure Chart Widget"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!isValid}>
              Save Widget
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Basic Settings */}
        <Container header={<Header variant="h2">Basic Settings</Header>}>
          <SpaceBetween direction="vertical" size="s">
            <FormField label="Widget Title" errorText={!title.trim() ? "Title is required" : undefined}>
              <Input
                value={title}
                onChange={({ detail }) => setTitle(detail.value)}
                placeholder="Enter widget title..."
              />
            </FormField>

            <FormField label="Description (optional)">
              <Textarea
                value={description}
                onChange={({ detail }) => setDescription(detail.value)}
                placeholder="Enter widget description..."
                rows={2}
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* Data Queries */}
        <Container 
          header={
            <Header 
              variant="h2"
              actions={
                <Button onClick={handleAddQuery} iconName="add-plus">
                  Add Data Series
                </Button>
              }
            >
              Data Sources
            </Header>
          }
        >
          {queries.length === 0 ? (
            <Box textAlign="center" color="text-body-secondary" padding="l">
              <SpaceBetween size="m">
                <div>No data series configured</div>
                <div style={{ fontSize: '12px' }}>
                  Add data series to display timeseries data on your chart
                </div>
              </SpaceBetween>
            </Box>
          ) : (
            <SpaceBetween direction="vertical" size="m">
              {queries.map((query, index) => {
                const properties = assetProperties[query.asset.id] || []
                
                return (
                  <Container key={query.id}>
                    <SpaceBetween direction="vertical" size="m">
                      <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
                        <FormField label="Series Name">
                          <Input
                            value={query.name || ''}
                            onChange={({ detail }) => 
                              handleQueryChange(query.id, { name: detail.value })
                            }
                            placeholder="Enter series name..."
                          />
                        </FormField>
                        
                        <FormField label="Actions">
                          <Button
                            iconName="remove"
                            variant="icon"
                            onClick={() => handleRemoveQuery(query.id)}
                          />
                        </FormField>
                      </Grid>

                      <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                        <FormField label="Asset" description="Select equipment to chart">
                          <Select
                            selectedOption={
                              query.asset.id ? {
                                label: query.asset.hierarchy.join(' > '),
                                value: query.asset.id
                              } : null
                            }
                            onChange={async ({ detail }) => {
                              const selectedAsset = assetHierarchy.find(a => a.id === detail.selectedOption.value)
                              if (selectedAsset) {
                                handleQueryChange(query.id, {
                                  asset: {
                                    id: selectedAsset.id,
                                    name: selectedAsset.name,
                                    hierarchy: selectedAsset.hierarchy,
                                    external_asset_id: selectedAsset.external_asset_id
                                  },
                                  // Reset property when asset changes
                                  property: {
                                    id: '',
                                    name: '',
                                    unit: '',
                                    dataType: 'number',
                                    propertyType: 'measurement'
                                  }
                                })
                                
                                // Load properties for this asset
                                await loadAssetProperties(selectedAsset.id)
                              }
                            }}
                            options={assetHierarchy.map(asset => ({
                              label: asset.hierarchy.join(' > '),
                              value: asset.id,
                              description: 'Equipment component'
                            }))}
                            loadingText={isLoadingAssets ? "Loading assets..." : undefined}
                            disabled={isLoadingAssets}
                            placeholder="Select asset..."
                            empty="No assets available"
                          />
                        </FormField>

                        <FormField label="Property" description="Select data property">
                          <Select
                            selectedOption={
                              query.property.id && properties.length > 0 ? {
                                label: `${query.property.name} (${query.property.unit})`,
                                value: query.property.id
                              } : null
                            }
                            onChange={({ detail }) => {
                              const selectedProperty = properties.find(p => p.id === detail.selectedOption.value)
                              if (selectedProperty) {
                                handleQueryChange(query.id, {
                                  property: {
                                    id: selectedProperty.id,
                                    name: selectedProperty.name,
                                    unit: selectedProperty.unit,
                                    dataType: selectedProperty.dataType,
                                    propertyType: selectedProperty.propertyType
                                  }
                                })
                              }
                            }}
                            options={properties.map(prop => ({
                              label: `${prop.name} (${prop.unit})`,
                              value: prop.id,
                              description: `Type: ${prop.dataType}`
                            }))}
                            loadingText={isLoadingProperties ? "Loading properties..." : undefined}
                            disabled={!query.asset.id || isLoadingProperties}
                            placeholder={query.asset.id ? "Select property..." : "Select asset first"}
                            empty={query.asset.id ? "No properties available" : "Select an asset first"}
                          />
                        </FormField>
                      </Grid>

                      <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                        <FormField label="Color">
                          <input
                            type="color"
                            value={query.color || '#1f77b4'}
                            onChange={(e) => 
                              handleQueryChange(query.id, { color: e.target.value })
                            }
                            style={{
                              width: '100%',
                              height: '32px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          />
                        </FormField>

                        <FormField label="Y-Axis">
                          <Select
                            selectedOption={{
                              label: query.yAxis === 'left' ? 'Left' : 'Right',
                              value: query.yAxis
                            }}
                            onChange={({ detail }) => 
                              handleQueryChange(query.id, { yAxis: detail.selectedOption.value })
                            }
                            options={[
                              { label: 'Left', value: 'left' },
                              { label: 'Right', value: 'right' }
                            ]}
                          />
                        </FormField>

                        <FormField label="Visibility">
                          <Toggle
                            checked={query.visible !== false}
                            onChange={({ detail }) => 
                              handleQueryChange(query.id, { visible: detail.checked })
                            }
                          >
                            Show in chart
                          </Toggle>
                        </FormField>
                      </Grid>
                    </SpaceBetween>
                  </Container>
                )
              })}
            </SpaceBetween>
          )}
        </Container>

        {/* Chart Settings */}
        <ExpandableSection headerText="Chart Settings" defaultExpanded={false}>
          <SpaceBetween direction="vertical" size="m">
            <FormField label="Chart Type">
              <Select
                selectedOption={
                  CHART_TYPE_OPTIONS.find(opt => opt.value === visualization.chartType) || CHART_TYPE_OPTIONS[0]
                }
                onChange={({ detail }) => 
                  handleVisualizationChange('chartType', detail.selectedOption?.value || 'line')
                }
                options={CHART_TYPE_OPTIONS}
              />
            </FormField>

            <FormField label="Line Thickness">
              <Slider
                value={visualization.strokeWidth || 2}
                onChange={({ detail }) => 
                  handleVisualizationChange('strokeWidth', detail.value)
                }
                min={1}
                max={5}
                step={0.5}
                hideFillLine
              />
            </FormField>

            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label="Show Legend">
                <Toggle
                  checked={visualization.legend?.show !== false}
                  onChange={({ detail }) => 
                    handleVisualizationChange('legend', { 
                      ...visualization.legend, 
                      show: detail.checked 
                    })
                  }
                />
              </FormField>

              <FormField label="Show Data Points">
                <Toggle
                  checked={visualization.showDataPoints || false}
                  onChange={({ detail }) => 
                    handleVisualizationChange('showDataPoints', detail.checked)
                  }
                />
              </FormField>
            </Grid>

            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label="Enable Zoom">
                <Toggle
                  checked={visualization.zoom?.enabled !== false}
                  onChange={({ detail }) => 
                    handleVisualizationChange('zoom', { 
                      ...visualization.zoom, 
                      enabled: detail.checked 
                    })
                  }
                />
              </FormField>

              <FormField label="Connect Null Values">
                <Toggle
                  checked={visualization.connectNulls || false}
                  onChange={({ detail }) => 
                    handleVisualizationChange('connectNulls', detail.checked)
                  }
                />
              </FormField>
            </Grid>
          </SpaceBetween>
        </ExpandableSection>

        {/* Preview/Validation */}
        {queries.length === 0 && (
          <Alert
            type="warning"
            statusIconAriaLabel="Warning"
          >
            Add at least one data series to display chart data. Without data sources, 
            the chart will show a "No data available" message.
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  )
}